import { useLocation, useMatch } from "react-router-dom";
import { useMemo } from "react";

import { appURLs } from "../../lib/appRoutes/appUrls";
import { useSafeNavigate, type SafeNavigate } from "./useSafeNavigate";

export interface ITrialsRouter {
  goBack: () => void;
  goToTrialsList: () => void;
  /** goToPlotDesign opens the editor instead of resuming the saved wizard step. */
  goToTrialsEditPage: (
    subpath?: string,
    chosenTrialId?: string,
    isUpdateTrialSuccess?: boolean | undefined,
    goToSummary?: boolean | undefined,
    goToPlotDesign?: boolean
  ) => void;
  goToTrialsCreatePage: (
    subpath?: string,
    isCreateTrialSuccess?: boolean | undefined
  ) => void;
}

/**
 * to get the router for trials module
 * @returns An object containing the trials router, current location, workspaceId, orgId, farmId, trialId, and navigate function.
 */
export const useTrialsRouter = (): {
  router: ITrialsRouter;
  location: ReturnType<typeof useLocation>;
  workspaceId?: string;
  orgId?: string;
  farmId?: string;
  trialId?: string;
  navigate: SafeNavigate;
} => {
  const navigate = useSafeNavigate();
  const location = useLocation();

  const match = useMatch(
    `${appURLs.TRIALS}/workspace/:workspaceId/org/:orgId/farm/:farmId/*`
  );
  const { workspaceId, orgId, farmId }: Record<string, string | undefined> =
    match?.params ?? {};

  const { trialId }: Record<string, string | undefined> =
    useMatch(
      `${appURLs.TRIALS}/workspace/:workspaceId/org/:orgId/farm/:farmId/trial/:trialId/*`
    )?.params ?? {};

  const trialsListingRoute = `${appURLs.TRIALS}/workspace/${
    workspaceId as string
  }/org/${orgId as string}/farm/${farmId as string}`;
  const editTrialRoute = `${appURLs.TRIALS}/workspace/${
    workspaceId as string
  }/org/${orgId as string}/farm/${farmId as string}/trial`;

  const getSubPathValue = (subpath: string | undefined): string =>
    subpath ? `/${subpath}` : "";

  const router: ITrialsRouter = useMemo(
    () => ({
      goBack: () => {
        navigate(-1);
      },
      goToTrialsEditPage: (
        subpath?: string,
        chosenTrialId?: string,
        isUpdateTrialSuccess?: boolean | undefined,
        goToSummary?: boolean | undefined,
        goToPlotDesign?: boolean
      ) => {
        const path = `${editTrialRoute}/${
          (chosenTrialId as string) ?? (trialId as string)
        }${getSubPathValue(subpath)}`;
        navigate(path, {
          state: {
            isUpdateTrialSuccess,
            goToSummary,
            goToPlotDesign,
          },
        });
      },
      goToTrialsCreatePage: (
        subpath?: string,
        isCreateTrialSuccess?: boolean | undefined
      ) => {
        const path = `${trialsListingRoute}/create${getSubPathValue(subpath)}`;
        navigate(path, {
          state: {
            isCreateTrialSuccess,
          },
        });
      },
      goToTrialsList: () => {
        navigate(trialsListingRoute);
      },
    }),
    [trialsListingRoute, editTrialRoute, navigate, trialId]
  );

  return { router, location, workspaceId, orgId, farmId, trialId, navigate };
};
